import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';

// Retain native form values, validation, labels and change events; render the popup in IO style.
const IOSelect = forwardRef(function IOSelect({ children, className = '', onPointerDown, onKeyDown, onClick, ...props }, forwardedRef) {
  const selectRef = useRef(null);
  const triggerRef = useRef(null);
  const [selectionLabel, setSelectionLabel] = useState('');
  const [fieldLabel, setFieldLabel] = useState('');
  const popupRef = useRef(null);
  const attachRef = useCallback(node => { selectRef.current = node; if (typeof forwardedRef === 'function') forwardedRef(node); else if (forwardedRef) forwardedRef.current = node; }, [forwardedRef]);
  const searchRef = useRef({ text: '', time: 0 });
  const initialFocus = useRef(null);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const popupId = useId();
  const [position, setPosition] = useState({});
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = triggerRef.current.getBoundingClientRect();
      const width = Math.min(Math.max(r.width, 160), window.innerWidth - 24);
      const below = window.innerHeight - r.bottom - 18;
      const above = r.top - 18;
      const height = Math.max(40, Math.min(320, below >= 160 || below >= above ? below : above));
      const up = below < 160 && above > below;
      setPosition({ position:'fixed', width, maxWidth:window.innerWidth - 24, maxHeight:height,
        left:Math.max(12, Math.min(r.left, window.innerWidth - width - 12)),
        ...(up ? {bottom:Math.max(12,window.innerHeight-r.top+6)} : {top:Math.max(12,r.bottom+6)}) });
    };
    place();
    window.addEventListener('resize',place);
    window.addEventListener('scroll',place,true);
    return () => { window.removeEventListener('resize',place); window.removeEventListener('scroll',place,true); };
  }, [open]);
  useLayoutEffect(() => { if (open) popupRef.current?.querySelector(`[data-option-index="${initialFocus.current}"]`)?.focus({preventScroll:true}); }, [open]);
  useEffect(() => {
    if (!open) return;
    const outside = event => { if (!popupRef.current?.contains(event.target) && !triggerRef.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('pointerdown',outside);
    document.addEventListener('focusin',outside);
    return () => { document.removeEventListener('pointerdown',outside); document.removeEventListener('focusin',outside); };
  }, [open]);
  const focusOption = index => popupRef.current?.querySelector(`[data-option-index="${index}"]`)?.focus();
  const show = key => {
    if (props.disabled) return;
    const items = Array.from(selectRef.current.options).filter(option => !option.hidden).map(option => ({
      value: option.value, label: option.text, selected: option.selected,
      disabled: option.disabled || option.parentElement?.disabled,
      group: option.parentElement?.tagName === 'OPTGROUP' ? option.parentElement.label : '',
    }));
    const enabled = items.map((item, index) => item.disabled ? -1 : index).filter(index => index >= 0);
    let index = items.findIndex(item => item.selected && !item.disabled);
    if (key?.length === 1 && key !== ' ') index = items.findIndex(item => !item.disabled && item.label.toLocaleLowerCase().startsWith(key.toLocaleLowerCase()));
    initialFocus.current = index >= 0 ? index : enabled[0];
    setOptions(items); setOpen(true);
  };
  const choose = index => {
    if (options[index]?.disabled) return;
    const select = selectRef.current;
    // Use the real control so existing handlers receive target.name/id/value unchanged.
    Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value').set.call(select, options[index].value);
    select.dispatchEvent(new Event('change', { bubbles: true }));
    setSelectionLabel(select.selectedOptions[0]?.text || '—');
    setOpen(false); triggerRef.current?.focus({preventScroll:true});
  };
  const onMenuKey = event => {
    event.stopPropagation();
    const enabled = options.map((item, index) => item.disabled ? -1 : index).filter(index => index >= 0);
    const current = Number(document.activeElement?.dataset.optionIndex);
    const position = enabled.indexOf(current);
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? enabled.length - 1
        : (position + (event.key === 'ArrowDown' ? 1 : -1) + enabled.length) % enabled.length;
      focusOption(enabled[next]);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault(); if (enabled.includes(current)) choose(current);
    } else if (event.key === 'Escape') {
      event.preventDefault(); setOpen(false); triggerRef.current?.focus({preventScroll:true});
    } else if (event.key === 'Tab') {
      setOpen(false); triggerRef.current?.focus({preventScroll:true});
    } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      const now = Date.now();
      const text = (now - searchRef.current.time < 700 ? searchRef.current.text : '') + event.key.toLocaleLowerCase();
      searchRef.current = { text, time: now };
      const match = enabled.find(index => options[index].label.toLocaleLowerCase().startsWith(text));
      if (match !== undefined) focusOption(match);
    }
  };
  // The native select carries form values only. Never focus it: iOS can open
  // its system picker even after pointerdown.preventDefault().
  useLayoutEffect(() => {
    const select = selectRef.current;
    setSelectionLabel(select.selectedOptions[0]?.text || '—');
    setFieldLabel(Array.from(select.labels || []).map(label => label.textContent).join(' '));
  });
  useEffect(() => {
    const select = selectRef.current;
    const labels = Array.from(select.labels || []);
    const focusTrigger = event => { event.preventDefault(); triggerRef.current?.focus({preventScroll:true}); };
    labels.forEach(label => label.addEventListener('click', focusTrigger));
    let resetTimer;
    const reset = () => { resetTimer = setTimeout(() => { setSelectionLabel(select.selectedOptions[0]?.text || '—'); setOpen(false); }, 0); };
    const form = select.form;
    form?.addEventListener('reset', reset);
    return () => { labels.forEach(label => label.removeEventListener('click', focusTrigger)); form?.removeEventListener('reset', reset); clearTimeout(resetTimer); };
  }, [props.id, props.form]);
  return <>
      <select {...props} ref={attachRef} hidden tabIndex={-1} aria-hidden="true" data-testid={undefined}
        style={{display:'none'}}
        onInvalid={event => { props.onInvalid?.(event); if (!event.defaultPrevented) { event.preventDefault(); triggerRef.current?.focus(); show(); } }}>
        {children}
      </select>
      <button ref={triggerRef} type="button" disabled={props.disabled} style={props.style}
        className={`io-select io-select-trigger ${className}`} data-ui-variant="select"
        data-testid={props['data-testid']} title={props.title} tabIndex={props.tabIndex}
        aria-label={props['aria-label'] || fieldLabel || props.title || undefined}
        aria-labelledby={props['aria-labelledby']} aria-describedby={props['aria-describedby']}
        aria-invalid={props['aria-invalid']} aria-required={props.required || undefined}
        aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? popupId : undefined}
        onPointerDown={onPointerDown}
        onClick={event => { onClick?.(event); if (!event.defaultPrevented) { if (open) setOpen(false); else show(); } }}
        onKeyDown={event => { onKeyDown?.(event); if (!event.defaultPrevented && !event.ctrlKey && !event.metaKey && (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key) || event.key.length === 1)) { event.preventDefault(); event.stopPropagation(); show(event.key); } }}>
        {selectionLabel}
      </button>
    {open && createPortal(
      <div ref={popupRef} id={popupId} role="listbox" aria-label={props['aria-label'] || selectRef.current?.labels?.[0]?.textContent || props.title || 'Auswahl'}
        className="io-menu-surface io-select-menu" style={position} onKeyDown={onMenuKey}>
        {options.map((option, index) => <React.Fragment key={`${option.value}-${index}`}>
          {option.group && option.group !== options[index - 1]?.group && <div className="io-select-group">{option.group}</div>}
          <div role="option" aria-selected={option.selected} aria-disabled={option.disabled || undefined} tabIndex={-1}
            data-option-index={index} className="io-select-option" onClick={() => choose(index)}>
            <span>{option.label || '—'}</span>{option.selected && <Check aria-hidden="true" size={16} />}
          </div>
        </React.Fragment>)}
      </div>, document.body)}
  </>;
});
export default IOSelect;
